/**
 * Collection Sharing Service
 * API wrapper for shared collections (group sharing and guest access)
 */

import { getStoredGuestToken } from '../utils/guestFingerprint';
import API_BASE_URL from '../config';

// ============================================================================
// TYPES
// ============================================================================

export interface SharedCollection {
  id: number;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  is_checklist?: boolean;
  stock_keeping?: boolean;
  owner_id: number;
  owner_name: string;
  permission: 'view' | 'edit' | 'full_edit';
  shared_at: string;
  group_id: number;
  group_name: string;
  last_activity_at: string;
  item_count: number;
  collection_type?: string;
}

export interface GuestLink {
  id: number;
  share_token: string;
  permission: 'view' | 'edit' | 'full_edit';
  expires_at: string | null;
  max_uses: number | null;
  use_count: number;
  last_used_at: string | null;
  created_at: string;
  is_active: boolean;
  require_email_verification?: boolean;
  verified_guest_emails?: { [email: string]: string };
}

export interface GuestCollection {
  collection: {
    id: number;
    name: string;
    description?: string;
    icon?: string;
    color?: string;
    is_checklist?: boolean;
    stock_keeping?: boolean;
    owner_id: number;
    collection_type?: string;
    owner_name: string;
    owner_email?: string;
    owner_location?: string;
    distance?: { miles: number; label: string } | null;
  };
  items: GuestCollectionItem[];
  permission: 'view' | 'edit' | 'full_edit';
  expires_at: string | null;
  in_grace_period?: boolean;
}

export interface GuestCollectionItem {
  id: number;
  name: string;
  description?: string;
  categories?: Array<{ id: number; key: string; label: string }>;
  quantity: number;
  sale_price?: number | null;
  image_id?: number;
  image_url?: string;
  thumbnail_url?: string;
  is_checked: boolean;
  checked_at: string | null;
  added_at: string;
  added_by: number | null;
  added_by_guest: string | null;
  added_by_name: string | null;
  item_owner_id: number;
  item_owner_name: string | null;
  item_owner_fingerprint: string | null;
  item_owner_user_id: number | null;
  item_owner_guest_token: string | null;
}

export interface ShareCollectionData {
  group_id: number;
  permission: 'view' | 'edit' | 'full_edit';
}

export interface CreateGuestLinkData {
  permission: 'view' | 'edit' | 'full_edit';
  expires_at?: string;
  max_uses?: number;
  require_email_verification?: boolean;
}

export interface AddItemAsGuestData {
  name: string;
  description?: string;
  category?: string;
  quantity?: number;
  guest_name?: string;
}

// ============================================================================
// GROUP SHARING
// ============================================================================

export const shareCollectionWithGroup = async (
  token: string,
  collectionId: number,
  data: ShareCollectionData
): Promise<{ success: boolean; shared_collection_id: number; shared_at: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/share`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to share collection');
  }

  return response.json();
};

export const unshareCollectionFromGroup = async (
  token: string,
  collectionId: number,
  groupId: number
): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/share/${groupId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to unshare collection');
  }

  return response.json();
};

export const updateCollectionSharingPermission = async (
  token: string,
  collectionId: number,
  groupId: number,
  permission: 'view' | 'edit' | 'full_edit'
): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/share/${groupId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ permission })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update permission');
  }

  return response.json();
};

export const getSharedCollections = async (token: string): Promise<SharedCollection[]> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/shared`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch shared collections');
  }

  const data = await response.json();
  return data.collections;
};

export interface CollectionSharedGroup {
  id: number;
  name: string;
  permission: 'view' | 'edit' | 'full_edit';
  shared_at: string;
}

export const getCollectionSharedGroups = async (
  token: string,
  collectionId: number
): Promise<CollectionSharedGroup[]> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/shared-groups`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch shared groups');
  }

  const data = await response.json();
  return data.groups;
};

// ============================================================================
// GUEST ACCESS
// ============================================================================

export const createGuestLink = async (
  token: string,
  collectionId: number,
  data: CreateGuestLinkData
): Promise<{
  success: boolean;
  id: number;
  share_token: string;
  share_url: string;
  created_at: string;
  expires_at: string | null;
}> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/guest-link`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create guest link');
  }

  return response.json();
};

export const getCollectionByGuestToken = async (token: string): Promise<GuestCollection> => {
  // Get device fingerprint for use count tracking
  const { getGuestFingerprint } = await import('./guestAuth');
  const fingerprint = await getGuestFingerprint();
  
  const response = await fetch(`${API_BASE_URL}/api/collections/guest/${token}`, {
    headers: {
      'X-Device-Fingerprint': fingerprint
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch collection');
  }

  const data = await response.json();
  
  // Add unsplash_url to items that have photographer data
  if (data.items) {
    data.items = data.items.map((item: any) => {
      if (item.unsplash_photo_id && item.photographer_username) {
        return {
          ...item,
          unsplash_url: `https://unsplash.com/photos/${item.unsplash_photo_id}`
        };
      }
      return item;
    });
  }
  
  return data;
};

export const addItemAsGuest = async (
  token: string,
  data: AddItemAsGuestData,
  deviceFingerprint: string,
  existingGuestToken?: string
): Promise<{ success: boolean; item_id: number; user_id?: number; guest_token?: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/guest/${token}/items`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      ...data,
      device_fingerprint: deviceFingerprint,
      guest_token: existingGuestToken  // Send existing token if available
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to add item');
  }

  return response.json();
};

export const removeItemAsGuest = async (
  token: string,
  itemId: number,
  deviceFingerprint: string
): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/guest/${token}/items/${itemId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      device_fingerprint: deviceFingerprint
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to remove item');
  }

  return response.json();
};

export const updateItemImageAsGuest = async (
  token: string,
  itemId: number,
  deviceFingerprint: string,
  options: {
    imageUrl?: string;
    useInitials?: boolean;
  }
): Promise<{ success: boolean; item_id: number; image_id: number | null }> => {
  const guestToken = getStoredGuestToken();
  
  const response = await fetch(`${API_BASE_URL}/api/collections/guest/${token}/items/${itemId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      device_fingerprint: deviceFingerprint,
      guest_token: guestToken || undefined,
      image_url: options.imageUrl,
      use_initials: options.useInitials
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update item');
  }

  return response.json();
};

export const updateItemAsGuest = async (
  token: string,
  itemId: number,
  deviceFingerprint: string,
  options: {
    name?: string;
    description?: string;
    imageUrl?: string;
    useInitials?: boolean;
    quantity?: number;
  }
): Promise<{ success: boolean; item_id: number; image_id: number | null }> => {
  const guestToken = getStoredGuestToken();
  
  const response = await fetch(`${API_BASE_URL}/api/collections/guest/${token}/items/${itemId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      device_fingerprint: deviceFingerprint,
      guest_token: guestToken || undefined,
      name: options.name,
      description: options.description,
      image_url: options.imageUrl,
      use_initials: options.useInitials,
      quantity: options.quantity
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update item');
  }

  return response.json();
};

export const getGuestLinks = async (
  token: string,
  collectionId: number
): Promise<GuestLink[]> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/guest-links`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch guest links');
  }

  const data = await response.json();
  return data.links;
};

export const revokeGuestLink = async (
  token: string,
  collectionId: number,
  linkId: number
): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/guest-links/${linkId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to revoke guest link');
  }

  return response.json();
};

export const deleteGuestLink = async (
  token: string,
  collectionId: number,
  linkId: number
): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/guest-links/${linkId}/permanent`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete guest link');
  }

  return response.json();
};

export const renewGuestLink = async (
  token: string,
  collectionId: number,
  linkId: number
): Promise<{ success: boolean; id: number; expires_at: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/guest-link/${linkId}/renew`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to renew guest link');
  }

  return response.json();
};

/**
 * Send email verification code for guest access
 */
export const sendGuestVerificationCode = async (
  email: string,
  shareToken: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/guest-access/verify/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, share_token: shareToken })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send verification code');
    }

    return await response.json();
  } catch (error: any) {
    console.error('Error sending verification code:', error);
    throw new Error(error.message || 'Failed to send verification code');
  }
};

/**
 * Confirm email verification code for guest access
 */
export const confirmGuestVerificationCode = async (
  email: string,
  code: string,
  shareToken: string
): Promise<{ success: boolean; email: string; collection_id: number }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/guest-access/verify/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code, share_token: shareToken })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to verify code');
    }

    return await response.json();
  } catch (error: any) {
    console.error('Error confirming verification code:', error);
    throw new Error(error.message || 'Failed to verify code');
  }
};

/**
 * Check if email is already verified for a guest link
 */
export const checkGuestVerification = async (
  email: string,
  shareToken: string
): Promise<{ verified: boolean; email?: string; verified_at?: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/guest-access/verify/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, share_token: shareToken })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to check verification');
    }

    return await response.json();
  } catch (error: any) {
    console.error('Error checking verification:', error);
    throw new Error(error.message || 'Failed to check verification');
  }
};

// ============================================================================
// MARKETPLACE PUBLIC ACCESS
// ============================================================================

export const getMarketplaceCollection = async (
  collectionId: number
): Promise<GuestCollection> => {
  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = token ? { 'Authorization': `Bearer ${token}` } : {};
  const response = await fetch(`${API_BASE_URL}/api/collections/marketplace/public/${collectionId}`, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Depot not found' }));
    throw new Error(error.error || 'Failed to load depot');
  }

  return response.json();
};

// ============================================================================
// MARKETPLACE VISIBILITY
// ============================================================================

export const toggleMarketplaceVisible = async (
  token: string,
  collectionId: number,
  marketplaceVisible: boolean
): Promise<{ success: boolean; marketplace_visible: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/marketplace-visible`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ marketplace_visible: marketplaceVisible })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update marketplace visibility');
  }

  return response.json();
};

// ============================================================================
// CHECK-OFF ITEMS (Shopping List Feature)
// ============================================================================

export const toggleItemCheckGuest = async (
  token: string,
  itemId: number,
  isChecked: boolean
): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/guest/${token}/items/${itemId}/check`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ is_checked: isChecked })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update item');
  }

  return response.json();
};

// ============================================================================
// COLLECTION TRANSFER
// ============================================================================

export interface CollectionTransfer {
  id: number;
  to_email: string;
  keep_copy: boolean;
  status: 'pending' | 'accepted' | 'cancelled' | 'expired';
  created_at: string;
  expires_at: string;
  message: string | null;
}

export interface TransferInfo {
  collection_name: string;
  icon?: string;
  color?: string;
  from_name: string;
  to_email: string;
  keep_copy: boolean;
  message: string | null;
  expires_at: string;
  item_count: number;
}

export const initiateCollectionTransfer = async (
  authToken: string,
  collectionId: number,
  toEmail: string,
  keepCopy: boolean,
  message?: string
): Promise<{ success: boolean; transfer_id: number; expires_at: string; email_sent: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/transfer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify({ to_email: toEmail, keep_copy: keepCopy, message: message || undefined })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to initiate transfer');
  }

  return response.json();
};

export const getCollectionTransfer = async (
  authToken: string,
  collectionId: number
): Promise<{ transfer: CollectionTransfer | null }> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/transfer`, {
    headers: { 'Authorization': `Bearer ${authToken}` }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch transfer');
  }

  return response.json();
};

export const cancelCollectionTransfer = async (
  authToken: string,
  collectionId: number
): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/transfer`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${authToken}` }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to cancel transfer');
  }

  return response.json();
};

export const getTransferInfo = async (
  token: string
): Promise<TransferInfo> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/transfer/${token}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Transfer not found');
  }

  return response.json();
};

export const acceptCollectionTransfer = async (
  authToken: string,
  token: string
): Promise<{ success: boolean; collection_id: number; collection_name: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/transfer/${token}/accept`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${authToken}` }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to accept transfer');
  }

  return response.json();
};

export const toggleItemCheckAuthenticated = async (
  token: string,
  collectionId: number,
  itemId: number,
  isChecked: boolean
): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}/items/${itemId}/check`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ is_checked: isChecked })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update item');
  }

  return response.json();
};
